import React, { useEffect, useState } from 'react';
import { Calendar, Plus, RefreshCw, Trash2, Download, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAvailabilityGroups } from '@/hooks/useAvailabilityGroups';
import { availabilityApi, type AvailabilityEffect } from '@/lib/availability';
import { useStandort } from '@/components/layouts/use-standort';
import { zeitraumText } from '@/lib/zeitraumText';

const AvailabilityGroupsSettings: React.FC = () => {
  const { standorte } = useStandort();
  const {
    groups,
    scopeRules,
    periods,
    selectedGroupId,
    setSelectedGroupId,
    loading,
    reload,
    loadPeriods,
  } = useAvailabilityGroups();

  const [showGroupForm, setShowGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupRegion, setNewGroupRegion] = useState('DE-NI');

  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [periodTitle, setPeriodTitle] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [periodFreq, setPeriodFreq] = useState<'NONE' | 'YEARLY'>('NONE');
  // Zeitraum, der gerade bearbeitet wird (T-0269); null = neuer Zeitraum.
  const [editingPeriodId, setEditingPeriodId] = useState<number | null>(null);

  // Ohne gewählte Gruppe blieben die Zeiträume unsichtbar (T-0268): die erste vorwählen.
  useEffect(() => {
    if (!selectedGroupId && groups.length > 0) setSelectedGroupId(groups[0].id);
  }, [groups, selectedGroupId, setSelectedGroupId]);

  const [showScopeForm, setShowScopeForm] = useState(false);
  const [scopeType, setScopeType] = useState<'global' | 'standort'>('standort');
  const [scopeStandortId, setScopeStandortId] = useState('');
  const [scopeGroupId, setScopeGroupId] = useState('');
  const [scopeEffect, setScopeEffect] = useState<AvailabilityEffect>('deny');
  const [importYear, setImportYear] = useState(String(new Date().getFullYear()));

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Name erforderlich');
      return;
    }
    try {
      const created = await availabilityApi.createGroup({
        name: newGroupName.trim(),
        regionCode: newGroupRegion || null,
        source: 'manual',
        aktiv: true,
      });
      toast.success('Gruppe erstellt');
      setShowGroupForm(false);
      setNewGroupName('');
      await reload();
      setSelectedGroupId(created.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fehler');
    }
  };

  const handleCreatePeriod = async () => {
    if (!selectedGroupId || !periodFrom || !periodTo) {
      toast.error('Gruppe und Datumsbereich erforderlich');
      return;
    }
    if (periodTo < periodFrom) {
      toast.error('Das Ende liegt vor dem Beginn');
      return;
    }
    try {
      const daten = {
        title: periodTitle || null,
        dateFrom: periodFrom,
        dateTo: periodTo,
        freq: periodFreq,
        defaultEffect: 'block' as const,
        aktiv: true,
      };
      if (editingPeriodId) {
        await availabilityApi.updatePeriod(selectedGroupId, editingPeriodId, daten);
        toast.success('Zeitraum gespeichert');
      } else {
        await availabilityApi.createPeriod(selectedGroupId, daten);
        toast.success('Zeitraum angelegt');
      }
      setShowPeriodForm(false);
      setEditingPeriodId(null);
      setPeriodTitle('');
      setPeriodFrom('');
      setPeriodTo('');
      setPeriodFreq('NONE');
      await loadPeriods(selectedGroupId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fehler');
    }
  };

  const handleCreateScopeRule = async () => {
    if (!scopeGroupId) {
      toast.error('Gruppe auswählen');
      return;
    }
    try {
      await availabilityApi.createScopeRule({
        scopeType,
        scopeId: scopeType === 'standort' && scopeStandortId ? Number(scopeStandortId) : null,
        groupId: Number(scopeGroupId),
        effect: scopeEffect,
        priority: scopeType === 'standort' ? 120 : 100,
        aktiv: true,
      });
      toast.success('Zuordnung gespeichert');
      setShowScopeForm(false);
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fehler');
    }
  };

  const handleImportHolidays = async () => {
    try {
      const result = await availabilityApi.importHolidays({
        year: Number(importYear),
        regionCode: newGroupRegion || 'DE',
        groupName: `Feiertage ${newGroupRegion} ${importYear}`,
      });
      toast.success(`${result.imported} Feiertage importiert (${result.skipped} übersprungen)`);
      await reload();
      setSelectedGroupId(result.groupId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Import fehlgeschlagen');
    }
  };

  const scopeLabel = (scopeType: string, standortName?: string | null) => {
    if (scopeType === 'global') return 'Global';
    if (scopeType === 'standort') return standortName ?? 'Standort';
    return 'Lehrer';
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Unterrichtsfreie Zeiten</h1>
        <p className="text-muted-foreground">
          Ferien-, Feiertags- und Schließgruppen mit Standort-Zuordnung. Lehrer können Gruppen und individuelle Ausnahmen zugeordnet bekommen.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Gruppen
          </CardTitle>
          <CardDescription>
            z. B. „Schulferien Niedersachsen“ oder „Feiertage“. Individuelle Lehrer-Overrides haben Vorrang vor Gruppenregeln.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowGroupForm(true)} disabled={showGroupForm}>
              <Plus className="mr-2 h-4 w-4" />Neue Gruppe
            </Button>
            <Button variant="outline" onClick={reload} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" />Aktualisieren
            </Button>
            <div className="flex items-center gap-2">
              <Input className="w-24" type="number" value={importYear} onChange={e => setImportYear(e.target.value)} />
              <Button variant="secondary" onClick={handleImportHolidays}>
                <Download className="mr-2 h-4 w-4" />Feiertage importieren
              </Button>
            </div>
          </div>

          {showGroupForm && (
            <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
              <Label>Name</Label>
              <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Schulferien Niedersachsen" />
              <Label>Region (z. B. DE-NI)</Label>
              <Input value={newGroupRegion} onChange={e => setNewGroupRegion(e.target.value)} />
              <div className="flex gap-2">
                <Button onClick={handleCreateGroup}>Erstellen</Button>
                <Button variant="outline" onClick={() => setShowGroupForm(false)}>Abbrechen</Button>
              </div>
            </div>
          )}

          <div className="divide-y rounded border">
            {groups.map(g => (
              <button
                key={g.id}
                type="button"
                className={`flex w-full items-center justify-between p-3 text-left hover:bg-muted/30 ${selectedGroupId === g.id ? 'bg-muted/40' : ''}`}
                onClick={() => setSelectedGroupId(g.id)}
              >
                <div>
                  <div className="font-medium">{g.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {g.regionCode ?? '—'} · {g.source} · {g.periodCount ?? 0} Zeiträume
                  </div>
                </div>
              </button>
            ))}
            {groups.length === 0 && !loading && (
              <div className="p-4 text-center text-sm text-muted-foreground">Keine Gruppen vorhanden</div>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedGroupId && (
        <Card>
          <CardHeader>
            <CardTitle>Zeiträume der Gruppe</CardTitle>
            <CardDescription>Einzelne Ferienwochen oder Feiertage. YEARLY = jährlich wiederkehrend (Monat/Tag).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={() => {
                setEditingPeriodId(null);
                setPeriodTitle('');
                setPeriodFrom('');
                setPeriodTo('');
                setPeriodFreq('NONE');
                setShowPeriodForm(true);
              }}
              disabled={showPeriodForm}
            >
              <Plus className="mr-2 h-4 w-4" />Zeitraum hinzufügen
            </Button>
            {showPeriodForm && (
              <div className="grid gap-3 rounded-lg border p-4 md:grid-cols-2">
                <div>
                  <Label>Titel</Label>
                  <Input value={periodTitle} onChange={e => setPeriodTitle(e.target.value)} placeholder="Sommerferien Woche 1" />
                </div>
                <div>
                  <Label>Wiederholung</Label>
                  <Select value={periodFreq} onValueChange={v => setPeriodFreq(v as 'NONE' | 'YEARLY')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">Einmalig (konkretes Jahr)</SelectItem>
                      <SelectItem value="YEARLY">Jährlich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Von</Label>
                  <Input type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)} />
                </div>
                <div>
                  <Label>Bis</Label>
                  <Input type="date" value={periodTo} onChange={e => setPeriodTo(e.target.value)} />
                </div>
                <div className="md:col-span-2 flex gap-2">
                  <Button onClick={handleCreatePeriod}>Speichern</Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPeriodForm(false);
                      setEditingPeriodId(null);
                    }}
                  >
                    Abbrechen
                  </Button>
                </div>
              </div>
            )}
            <div className="divide-y rounded border">
              {periods.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3">
                  <div>
                    {p.title ? <div className="font-medium">{p.title}</div> : null}
                    <div className={p.title ? 'text-sm text-muted-foreground' : 'font-medium'}>
                      {zeitraumText(p.dateFrom, p.dateTo, p.freq === 'YEARLY')}
                    </div>
                  </div>
                  <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Zeitraum bearbeiten"
                    onClick={() => {
                      setEditingPeriodId(p.id);
                      setPeriodTitle(p.title || '');
                      setPeriodFrom(p.dateFrom.slice(0, 10));
                      setPeriodTo((p.dateTo || p.dateFrom).slice(0, 10));
                      setPeriodFreq(p.freq === 'YEARLY' ? 'YEARLY' : 'NONE');
                      setShowPeriodForm(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    aria-label="Zeitraum löschen"
                    onClick={async () => {
                      if (!confirm('Zeitraum löschen?')) return;
                      await availabilityApi.deletePeriod(selectedGroupId, p.id);
                      await loadPeriods(selectedGroupId);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Standort- & globale Zuordnungen</CardTitle>
          <CardDescription>Welche Gruppe gilt für welchen Standort (oder global)?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={() => setShowScopeForm(true)} disabled={showScopeForm}>
            <Plus className="mr-2 h-4 w-4" />Zuordnung hinzufügen
          </Button>
          {showScopeForm && (
            <div className="grid gap-3 rounded-lg border p-4 md:grid-cols-2">
              <div>
                <Label>Scope</Label>
                <Select value={scopeType} onValueChange={v => setScopeType(v as 'global' | 'standort')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global</SelectItem>
                    <SelectItem value="standort">Standort</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {scopeType === 'standort' && (
                <div>
                  <Label>Standort</Label>
                  <Select value={scopeStandortId} onValueChange={setScopeStandortId}>
                    <SelectTrigger><SelectValue placeholder="Standort" /></SelectTrigger>
                    <SelectContent>
                      {standorte.map(s => (
                        <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Gruppe</Label>
                <Select value={scopeGroupId} onValueChange={setScopeGroupId}>
                  <SelectTrigger><SelectValue placeholder="Gruppe" /></SelectTrigger>
                  <SelectContent>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Wirkung</Label>
                <Select value={scopeEffect} onValueChange={v => setScopeEffect(v as AvailabilityEffect)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deny">Nicht verfügbar</SelectItem>
                    <SelectItem value="allow">Verfügbar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button onClick={handleCreateScopeRule}>Speichern</Button>
                <Button variant="outline" onClick={() => setShowScopeForm(false)}>Abbrechen</Button>
              </div>
            </div>
          )}
          <div className="divide-y rounded border">
            {scopeRules.filter(r => r.scopeType !== 'teacher').map(rule => (
              <div key={rule.id} className="flex items-center justify-between p-3">
                <div>
                  <div className="text-sm font-medium">
                    {scopeLabel(rule.scopeType, rule.standortName)} → {rule.groupName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {rule.effect === 'deny' ? 'Nicht verfügbar' : 'Verfügbar'} · Priorität {rule.priority}
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={async () => {
                    if (!confirm('Zuordnung löschen?')) return;
                    await availabilityApi.deleteScopeRule(rule.id);
                    await reload();
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AvailabilityGroupsSettings;
